import { Container, Stack } from "@mui/material";
import { useOutletContext } from "react-router";
import { useCallback, useRef, useState } from "react";
import {
  Button,
  ConfirmationModal,
  LoadingCard,
  Typography,
} from "@platform/design-system";

import { useBrokerSummaryWithCarrierQuery } from "../../hooks/useBbe";
import { ROUTE_NAMES } from "../../constants";
import { usePrompt } from "../../hooks";

import { BBEOutletContext } from "./types";
import { BrokerSettingCard } from "./components/brokerSettingCard";
import { BackNavigationButton } from "./components/BackNavigationButton";

export const BrokerSettings = (): React.JSX.Element => {
  const { brokerId } = useOutletContext<BBEOutletContext>();
  const { data, isLoading, refetch } =
    useBrokerSummaryWithCarrierQuery(brokerId);

  const [dirtyCards, setDirtyCards] = useState<Set<string>>(new Set());
  const [pending, isPending] = useState<boolean>(false);
  const saveHandlersRef = useRef<Map<string, () => Promise<void>>>(new Map());

  const registerSaveHandler = useCallback(
    (carrierId: string, handler: () => Promise<void>) => {
      saveHandlersRef.current.set(carrierId, handler);
    },
    [],
  );

  const handleDirtyChange = useCallback(
    (carrierId: string, isDirty: boolean) => {
      setDirtyCards((prev) => {
        const next = new Set(prev);
        if (isDirty) {
          next.add(carrierId);
        } else {
          next.delete(carrierId);
        }
        return next;
      });
    },
    [],
  );

  const hasUnsavedChanges = dirtyCards.size > 0;
  const [showPrompt, confirmNavigation, cancelNavigation] = usePrompt(
    hasUnsavedChanges,
  ) as [boolean, () => void, () => void];

  const handleSave = useCallback(async () => {
    isPending(true);
    try {
      const savePromises = Array.from(dirtyCards)
        .map((carrierId) => saveHandlersRef.current.get(carrierId))
        .filter(Boolean)
        .map((save) => save());
      await Promise.all(savePromises);
      confirmNavigation();
    } catch {
      // individual save handlers already show error toasts
    } finally {
      isPending(false);
    }
  }, [dirtyCards, confirmNavigation]);

  return (
    <Container>
      <Stack spacing={0}>
        <BackNavigationButton
          label="Bonus Forecast"
          route={`/${ROUTE_NAMES.BBE}/${brokerId}/${ROUTE_NAMES.BBE_FORECAST}`}
        />

        <Typography
          variant="header-medium"
          py={2}
          pt={0}
          pb={2}
          sx={{ fontWeight: 400 }}
        >
          Carrier Bonus Settings
        </Typography>
        {/* TODO: Update once we are able to retrieve */}
        {/* <Typography variant="body-medium" color="textSecondary">
                    Last salesforce sync {}
                  </Typography> */}
      </Stack>

      <Stack spacing={3} sx={{ width: "100%" }}>
        {isLoading ? (
          <LoadingCard
            action={null}
            gridItemProps={{ size: { xs: 12, sm: 6 } }}
            skeleton={{ count: 4, props: { height: 56 } }}
          />
        ) : (
          data?.map((d) => (
            <BrokerSettingCard
              key={d.carrierId}
              brokerSummary={d}
              refetch={refetch}
              onDirtyChange={handleDirtyChange}
              registerSaveHandler={registerSaveHandler}
              setIsPending={isPending}
            />
          ))
        )}
      </Stack>

      <ConfirmationModal
        maxWidth="xs"
        open={showPrompt}
        title="Save Changes?"
        description="Changes have not been saved. Do you want to save?"
        confirmButtonText="Save changes"
        cancelButtonText="Cancel"
        onAccept={confirmNavigation}
        onCancel={cancelNavigation}
        actions={
          <>
            <Button
              data-testid="blocker-modal-cancel-button"
              onClick={cancelNavigation}
              variant="tertiary"
              disabled={pending}
            >
              Cancel
            </Button>
            <Button
              data-testid="blocker-modal-confirm-button"
              onClick={confirmNavigation}
              variant="secondary"
              disabled={pending}
            >
              Discard
            </Button>
            <Button
              data-testid="blocker-modal-confirm-update-button"
              onClick={handleSave}
              variant="primary"
              loading={pending}
            >
              Save
            </Button>
          </>
        }
      />
    </Container>
  );
};

export default BrokerSettings;
